import React, { useState, useCallback, createContext, useContext } from 'react';
import './Toast.css';

// Toast Context
const ToastContext = createContext();

// Sanitize error messages to be more user-friendly
const sanitizeMessage = (message) => {
  if (!message) return 'An error occurred';

  // Convert to string if it's an object
  let msg = typeof message === 'string' ? message : String(message);

  // Remove technical stack traces
  msg = msg.split('\n')[0];

  // Shorten very long messages
  if (msg.length > 100) {
    msg = msg.substring(0, 97) + '...';
  }

  // Common error message replacements
  const replacements = {
    'Network Error': 'Connection failed',
    'Request failed with status code 500': 'Server error occurred',
    'Request failed with status code 404': 'Not found',
    'Request failed with status code 401': 'Authentication required',
    'Request failed with status code 403': 'Access denied',
    'ECONNREFUSED': 'Cannot connect to server',
    'timeout': 'Request timed out',
  };

  for (const [key, value] of Object.entries(replacements)) {
    if (msg.includes(key)) {
      msg = value;
      break;
    }
  }

  return msg;
};

// Toast Provider Component
export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);
  const [_currentToastId, setCurrentToastId] = useState(null);
  const timeoutRef = React.useRef(null);

  const addToast = useCallback((message, type = 'info', options = {}) => {
    const sanitizedMessage = sanitizeMessage(message);

    // Clear any existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }

    const id = Date.now() + Math.random();
    const toast = {
      id,
      message: sanitizedMessage,
      type, // success, error, warning, info
      duration: options.duration !== undefined ? options.duration : 3000,
      actions: options.actions || null,
      onClose: options.onClose || null,
      ...options
    };

    // CRITICAL: Replace all toasts with just this one (no duplicates)
    setToasts([toast]);
    setCurrentToastId(id);

    // Auto dismiss
    if (toast.duration > 0) {
      timeoutRef.current = setTimeout(() => {
        removeToast(id);
      }, toast.duration);
    }

    return id;
  // eslint-disable-next-line react-hooks/exhaustive-deps -- removeToast is defined below and stable
  }, []);

  const removeToast = useCallback((id) => {
    // Clear timeout if exists
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }

    setToasts(prev => prev.map(toast =>
      toast.id === id ? { ...toast, closing: true } : toast
    ));

    // Remove after animation
    setTimeout(() => {
      setToasts([]);
      setCurrentToastId(null);
    }, 300);
  }, []);

  const success = useCallback((message, options) => {
    return addToast(message, 'success', options);
  }, [addToast]);

  const error = useCallback((message, options) => {
    return addToast(message, 'error', options);
  }, [addToast]);

  const warning = useCallback((message, options) => {
    return addToast(message, 'warning', options);
  }, [addToast]);

  const info = useCallback((message, options) => {
    return addToast(message, 'info', options);
  }, [addToast]);

  const value = {
    toast: addToast,
    success,
    error,
    warning,
    info,
    remove: removeToast
  };

  return (
    <ToastContext.Provider value={value}>
      {children}
      <ToastContainer toasts={toasts} onRemove={removeToast} />
    </ToastContext.Provider>
  );
};

// Toast Container Component - Only show the LAST toast (most recent)
const ToastContainer = ({ toasts, onRemove }) => {
  if (toasts.length === 0) return null;

  // Only render the most recent toast (last in array)
  const latestToast = toasts[toasts.length - 1];

  // No inline position override here -- Toast.css's .toast-container already
  // owns top-center placement (top:20px, left:50%, translateX(-50%)); an
  // inline style previously forced top:20px/right:20px/left:auto/
  // transform:none instead, which always wins over the CSS class and
  // silently pinned every toast to the top-right regardless of what the
  // stylesheet said. Confirmed live.
  return (
    <div className="toast-container" style={{ zIndex: 2147483647 }}>
      <Toast key={latestToast.id} {...latestToast} onClose={() => onRemove(latestToast.id)} />
    </div>
  );
};

// Individual Toast Component
const Toast = ({ _id, message, type, actions, onClose, closing, duration }) => {
  const getIcon = () => {
    switch (type) {
      case 'success':
        return '✓';
      case 'error':
        return '✕';
      case 'warning':
        return '⚠';
      case 'info':
        return 'i';
      default:
        return 'i';
    }
  };

  // Fixed positioning wrapper for standalone Toast usage (also the one that
  // actually wins when rendered through ToastContainer above -- a nested
  // position:fixed element always positions from the viewport regardless of
  // its own fixed-position ancestor, so THIS wrapper's coordinates are the
  // real, single source of truth for on-screen placement either way).
  // Top-center, matching Toast.css's .toast-container -- this previously
  // hardcoded top-right, silently overriding the stylesheet's own top-center
  // intent for every toast in the app.
  const wrapperStyle = {
    position: 'fixed',
    top: '20px',
    left: '50%',
    transform: 'translateX(-50%)',
    zIndex: 2147483647,
    pointerEvents: 'none'
  };

  const toastStyle = {
    pointerEvents: 'auto'
  };

  return (
    <div style={wrapperStyle}>
      <div className={`toast ${type} ${closing ? 'closing' : ''}`} style={toastStyle}>
        <div className="toast-icon">{getIcon()}</div>
        <div className="toast-content">
          <p className="toast-message">{message}</p>
          {actions && actions.length > 0 && (
            <div className="toast-actions">
              {actions.map((action, index) => (
                <button
                  key={index}
                  className={`toast-action-button ${action.primary ? 'primary' : ''}`}
                  onClick={() => {
                    if (action.onClick) action.onClick();
                    onClose();
                  }}
                >
                  {action.label}
                </button>
              ))}
            </div>
          )}
        </div>
        <button className="toast-close" onClick={onClose}>×</button>
        {duration > 0 && (
          <div
            className="toast-progress"
            style={{ animationDuration: `${duration}ms` }}
          />
        )}
      </div>
    </div>
  );
};

// Custom hook to use toast
export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within ToastProvider');
  }
  return context;
};

export default Toast;
